import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "../hooks/useTheme";
import { useUpdateProfile } from "../hooks/useProfile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  User,
  Save,
  Moon,
  Sun,
  Monitor,
  Palette,
  Bell,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/apiErrors";
import { PushNotificationToggle } from "@/components/notifications";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import type { UpdateProfileRequest } from "../types/auth";

export const Profile: React.FC = () => {
  const { user, refreshUserProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const updateProfileMutation = useUpdateProfile();

  // User profile settings
  const [profileSettings, setProfileSettings] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
  });

  // Update profile settings when user changes
  useEffect(() => {
    if (user) {
      setProfileSettings((prev) => ({
        ...prev,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      }));
    }
  }, [user]);

  const handleSaveProfile = async () => {
    try {
      const updateData: UpdateProfileRequest = {
        firstName: profileSettings.firstName,
        lastName: profileSettings.lastName,
        theme: theme,
      };

      await updateProfileMutation.mutateAsync(updateData);

      // Refresh user profile in AuthContext
      await refreshUserProfile();

      toast({
        title: "Success",
        description: "Profile updated successfully.",
      });
    } catch (error) {
      console.error("Failed to save profile settings:", error);
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to update profile."),
        variant: "destructive",
      });
    }
  };

  const handleThemeChange = (newTheme: "light" | "dark" | "system") => {
    setTheme(newTheme);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <User className="h-6 w-6" />
          Profile
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Manage your personal account settings
        </p>
      </div>

      <div className="grid gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Update your personal details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={profileSettings.firstName}
                  onChange={(e) =>
                    setProfileSettings((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={profileSettings.lastName}
                  onChange={(e) =>
                    setProfileSettings((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={profileSettings.email}
                disabled
                className="bg-gray-50 dark:bg-gray-800"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Email address cannot be changed. Contact your administrator if
                needed.
              </p>
            </div>

            <div className="pt-4 border-t">
              <Button
                onClick={handleSaveProfile}
                disabled={updateProfileMutation.isPending}
                className="w-full sm:w-auto"
              >
                <Save className="mr-2 h-4 w-4" />
                {updateProfileMutation.isPending
                  ? "Saving..."
                  : "Save Profile"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <ChangePasswordCard />

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notifications
            </CardTitle>
            <CardDescription>
              Configure how you receive notifications.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <PushNotificationToggle />
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Appearance
            </CardTitle>
            <CardDescription>
              Choose how the interface should look.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              <Label>Theme</Label>
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div
                  className={`flex flex-col items-center p-2 sm:p-4 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${theme === "light" ? "border-blue-500 bg-blue-50 dark:bg-blue-950" : ""}`}
                  onClick={() => handleThemeChange("light")}
                >
                  <Sun className="h-5 w-5 sm:h-6 sm:w-6 mb-2" />
                  <span className="text-xs sm:text-sm">Light</span>
                </div>
                <div
                  className={`flex flex-col items-center p-2 sm:p-4 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${theme === "dark" ? "border-blue-500 bg-blue-50 dark:bg-blue-950" : ""}`}
                  onClick={() => handleThemeChange("dark")}
                >
                  <Moon className="h-5 w-5 sm:h-6 sm:w-6 mb-2" />
                  <span className="text-xs sm:text-sm">Dark</span>
                </div>
                <div
                  className={`flex flex-col items-center p-2 sm:p-4 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${theme === "system" ? "border-blue-500 bg-blue-50 dark:bg-blue-950" : ""}`}
                  onClick={() => handleThemeChange("system")}
                >
                  <Monitor className="h-5 w-5 sm:h-6 sm:w-6 mb-2" />
                  <span className="text-xs sm:text-sm">System</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Your theme preference is automatically saved.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
